import React, { useEffect, useState, useCallback } from 'react';

const FREQUENCIES = [
  { key: 'PER_STAY', label: 'Per Stay' },
  { key: 'PER_NIGHT', label: 'Per Night' },
  { key: 'PER_ROOM_SERVICE', label: 'Per Room Service' },
];

/**
 * Amenity Room-Type Template (docs/DEV_PMS_OPERATIONS_ENHANCEMENT.md Module 1)
 * — "Deluxe Room -> 2 water bottles/night" mapping. Mirrors HkChecklistMaster's
 * pattern: pick a room type, edit the item list, POST replaces the whole set
 * for that room type (server soft-deletes anything omitted).
 */
function HkAmenityTemplates({ token, selectedProperty }) {
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomTypeId, setRoomTypeId] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [rows, setRows] = useState([]); // { item_id, standard_qty, consumption_frequency }
  const [addItemId, setAddItemId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const loadRoomTypes = useCallback(async () => {
    if (!token || !selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/pms/room-types', { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (res.ok) {
        setRoomTypes(data);
        if (data.length && roomTypeId == null) setRoomTypeId(data[0].room_type_id);
      }
    } catch { /* no-op */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty, headers]);

  const loadItems = useCallback(async () => {
    if (!token || !selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/amenity-inventory/items', { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (res.ok) setAllItems(data);
    } catch { /* no-op */ }
  }, [token, selectedProperty, headers]);

  const loadTemplate = useCallback(async (rtId) => {
    if (!rtId) return;
    setLoading(true); setError('');
    try {
      const res = await fetch(`/api/v1/amenity-inventory/templates?room_type_id=${rtId}`, { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (!res.ok) { setError(data.error || 'Failed to load template'); return; }
      setRows(data.map(r => ({ item_id: r.item_id, item_name: r.item_name, unit: r.unit, standard_qty: r.standard_qty, consumption_frequency: r.consumption_frequency })));
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { loadRoomTypes(); loadItems(); }, [loadRoomTypes, loadItems]);
  useEffect(() => { if (roomTypeId) loadTemplate(roomTypeId); }, [roomTypeId, loadTemplate]);

  const updateRow = (itemId, patch) => {
    setRows(prev => prev.map(r => (r.item_id === itemId ? { ...r, ...patch } : r)));
    setSuccess('');
  };
  const removeRow = (itemId) => setRows(prev => prev.filter(r => r.item_id !== itemId));

  const addRow = () => {
    const itemId = Number(addItemId);
    if (!itemId || rows.some(r => r.item_id === itemId)) return;
    const item = allItems.find(i => i.item_id === itemId);
    setRows(prev => [...prev, { item_id: itemId, item_name: item?.item_name, unit: item?.unit, standard_qty: 1, consumption_frequency: 'PER_STAY' }]);
    setAddItemId('');
  };

  const save = async () => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      const res = await fetch('/api/v1/amenity-inventory/templates', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          room_type_id: roomTypeId,
          items: rows.map(r => ({ item_id: r.item_id, standard_qty: r.standard_qty, consumption_frequency: r.consumption_frequency })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to save'); return; }
      setSuccess('Template saved.');
      loadTemplate(roomTypeId);
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setSaving(false); }
  };

  const availableToAdd = allItems.filter(i => i.is_active && !rows.some(r => r.item_id === i.item_id));

  if (!selectedProperty?.id) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Select a property to configure amenity templates.</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 4 }}>Amenity Room-Type Template</h2>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
        Map standard amenity quantities per room type — e.g. Deluxe Room → 2 water bottles / night.
        These quantities pre-fill the housekeeping app's consumption confirm screen for each cleaning task.
      </p>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Room Type</label>
        <select value={roomTypeId || ''} onChange={e => setRoomTypeId(Number(e.target.value))} style={{ ...inputStyle, maxWidth: 320 }}>
          {roomTypes.map(rt => <option key={rt.room_type_id} value={rt.room_type_id}>{rt.room_type_name}</option>)}
        </select>
      </div>

      {error && <div style={{ background: '#fde8e8', color: '#b71c1c', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>{error}</div>}
      {success && <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>{success}</div>}

      {loading ? <p>Loading…</p> : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={thStyle}>Item</th>
                  <th style={thStyle}>Standard Qty</th>
                  <th style={thStyle}>Frequency</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.item_id}>
                    <td style={tdStyle}>{r.item_name} <span style={{ color: '#94a3b8', fontSize: 12 }}>({r.unit})</span></td>
                    <td style={tdStyle}>
                      <input type="number" min="0" value={r.standard_qty} onChange={e => updateRow(r.item_id, { standard_qty: Number(e.target.value) })} style={{ ...inputStyle, width: 100 }} />
                    </td>
                    <td style={tdStyle}>
                      <select value={r.consumption_frequency} onChange={e => updateRow(r.item_id, { consumption_frequency: e.target.value })} style={inputStyle}>
                        {FREQUENCIES.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <button onClick={() => removeRow(r.item_id)} style={{ border: 'none', background: 'none', color: '#b71c1c', cursor: 'pointer', fontWeight: 700 }}>×</button>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No items mapped for this room type yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={addItemId} onChange={e => setAddItemId(e.target.value)} style={{ ...inputStyle, maxWidth: 260 }}>
              <option value="">— add an item —</option>
              {availableToAdd.map(i => <option key={i.item_id} value={i.item_id}>{i.item_name}</option>)}
            </select>
            <button onClick={addRow} disabled={!addItemId}
              style={{ padding: '7px 14px', background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 4, cursor: addItemId ? 'pointer' : 'not-allowed', fontSize: 13.5, fontWeight: 600 }}>
              + Add
            </button>
            <button onClick={save} disabled={saving} style={{ marginLeft: 'auto', padding: '8px 24px', background: saving ? '#999' : '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
              {saving ? 'Saving…' : 'Save Template'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const thStyle = { padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px', borderBottom: '1px solid #e0e0e0' };
const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13.5, boxSizing: 'border-box' };

export default HkAmenityTemplates;
